/**
 * /dashboard/dsar — Org-admin triage queue for Data Subject Access
 * Requests (Batch 2 PR #5).
 *
 * Surfaces every DSAR row routed to the caller's org so owners /
 * admins can:
 *
 *   - browse the queue, filter by status / request type
 *   - see which rows are overdue (past the Art. 12(3) 30-day clock)
 *   - open a row in the side drawer for full detail
 *   - advance a row through the lifecycle (received →
 *     awaiting_clarification → in_progress → fulfilled / rejected)
 *   - apply a +60-day Art. 12(3) extension with the documented
 *     reason
 *   - record an internal note (operator-only) or a customer-facing
 *     note in the response_payload
 *
 * Wraps `GET /api/orgs/:id/dsar` + `PATCH /api/orgs/:id/dsar/:dsarId`.
 * Server enforces the role check (owner / admin) — a viewer who
 * navigates here gets a clean 403 banner rather than a crash.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ArrowLeft, FileText, AlertTriangle, Loader2, RefreshCw, Calendar, ShieldCheck } from 'lucide-react';
import { BrandMark } from '@rynora/theme';
import { getApiBaseUrl } from '../../../lib/config';

const API_BASE: string = getApiBaseUrl();

interface OrgDsarPageProps {
  onBack: () => void;
}

interface OrgSummary {
  id: string;
  slug: string;
  name: string;
  role?: 'owner' | 'admin' | 'member' | 'viewer';
}

type DsarStatus = 'received' | 'awaiting_clarification' | 'in_progress' | 'fulfilled' | 'rejected' | 'cancelled';
type RequestType = 'access' | 'rectification' | 'erasure' | 'restriction' | 'portability' | 'objection' | 'other';

interface DsarRow {
  id: string;
  ticketReference: string;
  orgId: string | null;
  submitterEmail: string;
  submitterName: string | null;
  submitterUserId: string | null;
  requestType: RequestType;
  description: string;
  status: DsarStatus;
  source: 'public_form' | 'authenticated_user' | 'admin_proxy';
  submittedAt: string;
  dueAt: string;
  extendedAt: string | null;
  extensionReason: string | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
  completedAt: string | null;
  responsePayload: Record<string, unknown> & {
    downloadUrl?: string;
    rejectionReason?: string;
    notes?: string;
  };
  internalNotes: string | null;
}

interface ListResponse {
  items: DsarRow[];
  nextCursor: string | null;
  openCount: number;
  overdueCount: number;
}

const STATUS_LABELS: Record<DsarStatus, string> = {
  received: 'Received',
  awaiting_clarification: 'Awaiting clarification',
  in_progress: 'In progress',
  fulfilled: 'Fulfilled',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  access: 'Access (Art. 15)',
  rectification: 'Rectification (Art. 16)',
  erasure: 'Erasure (Art. 17)',
  restriction: 'Restriction (Art. 18)',
  portability: 'Portability (Art. 20)',
  objection: 'Objection (Art. 21)',
  other: 'Other',
};

const TERMINAL: ReadonlySet<DsarStatus> = new Set(['fulfilled', 'rejected', 'cancelled']);

function readActiveOrgFromToken(): { orgId: string | null } {
  const token = localStorage.getItem('rynora_token');
  if (!token) return { orgId: null };
  const parts = token.split('.');
  if (parts.length < 2) return { orgId: null };
  try {
    const json = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { orgId: typeof json.activeOrgId === 'string' ? json.activeOrgId : null };
  } catch {
    return { orgId: null };
  }
}

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('rynora_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new Error(body.error || body.code || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function OrgDsarPage({ onBack }: OrgDsarPageProps) {
  const { orgId } = useMemo(readActiveOrgFromToken, []);

  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [list, setList] = useState<ListResponse | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<DsarStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<RequestType | ''>('');

  const [active, setActive] = useState<DsarRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch<{ orgs: OrgSummary[] }>('/api/orgs');
        if (cancelled) return;
        setOrgs(res.orgs);
        const initial = res.orgs.find((o) => o.id === orgId) || res.orgs[0] || null;
        setSelectedOrg(initial);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load organizations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const refresh = async () => {
    if (!selectedOrg) return;
    setListLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (typeFilter) qs.set('requestType', typeFilter);
      const path = `/api/orgs/${selectedOrg.id}/dsar${qs.toString() ? `?${qs.toString()}` : ''}`;
      const r = await authedFetch<ListResponse>(path);
      setList(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load DSAR queue.');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedOrg) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, statusFilter, typeFilter]);

  const canManage =
    selectedOrg && (selectedOrg.role === 'owner' || selectedOrg.role === 'admin' || selectedOrg.role === undefined);

  return (
    <div className="legal-shell">
      <header className="legal-header">
        <div className="container legal-header-inner">
          <BrandMark variant="compact" />
          <button type="button" className="nav-auth-btn" onClick={onBack}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </header>

      <main className="container" style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        <header>
          <h1 style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
            <FileText size={22} /> Data-request triage
          </h1>
          <p style={{ marginTop: 6, color: 'var(--text-muted)' }}>
            Queue of GDPR / CPRA / LGPD / PDPA data-subject requests routed to your organisation. The Art. 12(3) clock
            ticks from <code>submittedAt</code> — pay attention to anything in the <strong>Overdue</strong> row first.
          </p>
        </header>

        {loading && (
          <div className="dsar-loading">
            <Loader2 size={16} className="dsar-form__spinner" /> Loading…
          </div>
        )}

        {!loading && orgs.length > 1 && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>Workspace:</span>
            <select
              value={selectedOrg?.id ?? ''}
              onChange={(e) => setSelectedOrg(orgs.find((o) => o.id === e.target.value) ?? null)}
              style={{ padding: '6px 10px', borderRadius: 4 }}
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {error && (
          <div className="dsar-form__error" role="alert">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {!loading && selectedOrg && (
          <>
            <div className="dsar-queue__summary">
              <div>
                <span className="dsar-queue__summary-label">Open</span>
                <span className="dsar-queue__summary-value">{list?.openCount ?? 0}</span>
              </div>
              <div className={list && list.overdueCount > 0 ? 'is-overdue' : ''}>
                <span className="dsar-queue__summary-label">Overdue</span>
                <span className="dsar-queue__summary-value">{list?.overdueCount ?? 0}</span>
              </div>
              <button type="button" className="nav-auth-btn" onClick={refresh} disabled={listLoading}>
                <RefreshCw size={14} className={listLoading ? 'dsar-form__spinner' : ''} /> Refresh
              </button>
            </div>

            <div className="dsar-queue__filters">
              <label>
                <span>Status</span>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as DsarStatus | '')}>
                  <option value="">All</option>
                  {(Object.keys(STATUS_LABELS) as DsarStatus[]).map((k) => (
                    <option key={k} value={k}>
                      {STATUS_LABELS[k]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Type</span>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as RequestType | '')}>
                  <option value="">All</option>
                  {(Object.keys(REQUEST_TYPE_LABELS) as RequestType[]).map((k) => (
                    <option key={k} value={k}>
                      {REQUEST_TYPE_LABELS[k]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="dsar-queue__table" role="region" aria-label="DSAR queue">
              <table>
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Type</th>
                    <th>Submitter</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Due</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {(list?.items ?? []).map((row) => {
                    const overdue =
                      new Date(row.dueAt).getTime() < Date.now() &&
                      row.status !== 'fulfilled' &&
                      row.status !== 'rejected' &&
                      row.status !== 'cancelled';
                    return (
                      <tr key={row.id} onClick={() => setActive(row)} className={overdue ? 'is-overdue' : ''}>
                        <td>
                          <code title={row.ticketReference}>{row.ticketReference.slice(0, 8)}…</code>
                        </td>
                        <td>{REQUEST_TYPE_LABELS[row.requestType]}</td>
                        <td>{row.submitterEmail}</td>
                        <td>
                          <span className={`dsar-pill dsar-pill--${row.status}`}>{STATUS_LABELS[row.status]}</span>
                        </td>
                        <td>{new Date(row.submittedAt).toLocaleDateString()}</td>
                        <td>{new Date(row.dueAt).toLocaleDateString()}</td>
                        <td>{row.source}</td>
                      </tr>
                    );
                  })}
                  {list && list.items.length === 0 && !listLoading && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                        No data-subject requests match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {active && selectedOrg && (
              <DsarDrawer
                row={active}
                orgId={selectedOrg.id}
                canManage={!!canManage}
                onClose={() => setActive(null)}
                onUpdated={(next) => {
                  setActive(next);
                  void refresh();
                }}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

interface DrawerProps {
  row: DsarRow;
  orgId: string;
  canManage: boolean;
  onClose: () => void;
  onUpdated: (row: DsarRow) => void;
}

function DsarDrawer({ row, orgId, canManage, onClose, onUpdated }: DrawerProps) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [nextStatus, setNextStatus] = useState<DsarStatus>(row.status);
  const [internalNotes, setInternalNotes] = useState(row.internalNotes ?? '');
  const [downloadUrl, setDownloadUrl] = useState(row.responsePayload.downloadUrl ?? '');
  const [notes, setNotes] = useState(row.responsePayload.notes ?? '');
  const [rejectionReason, setRejectionReason] = useState(row.responsePayload.rejectionReason ?? '');

  const [extendDays, setExtendDays] = useState(0);
  const [extendReason, setExtendReason] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (TERMINAL.has(row.status)) {
      setErr('This request is in a terminal state and cannot be modified.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {};
      if (nextStatus !== row.status) body.status = nextStatus;
      if (internalNotes !== (row.internalNotes ?? '')) body.internalNotes = internalNotes;
      const responsePayload: Record<string, string> = {};
      if (downloadUrl !== (row.responsePayload.downloadUrl ?? '')) responsePayload.downloadUrl = downloadUrl;
      if (notes !== (row.responsePayload.notes ?? '')) responsePayload.notes = notes;
      if (rejectionReason !== (row.responsePayload.rejectionReason ?? ''))
        responsePayload.rejectionReason = rejectionReason;
      if (Object.keys(responsePayload).length > 0) body.responsePayload = responsePayload;
      if (extendDays > 0 && extendReason.trim().length >= 10) {
        body.extendForDays = extendDays;
        body.extensionReason = extendReason.trim();
      }
      if (Object.keys(body).length === 0) {
        setErr('Nothing to change.');
        return;
      }
      const res = await authedFetch<{ row: DsarRow }>(`/api/orgs/${orgId}/dsar/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      onUpdated(res.row);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dsar-drawer" role="dialog" aria-label={`DSAR ${row.ticketReference}`}>
      <header className="dsar-drawer__head">
        <div>
          <h2 style={{ margin: 0 }}>{REQUEST_TYPE_LABELS[row.requestType]}</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)' }}>
            Ticket <code>{row.ticketReference}</code> · from <strong>{row.submitterEmail}</strong>
          </p>
        </div>
        <button type="button" className="nav-auth-btn" onClick={onClose}>
          Close
        </button>
      </header>

      <section className="dsar-drawer__meta">
        <dl>
          <dt>Status</dt>
          <dd>
            <span className={`dsar-pill dsar-pill--${row.status}`}>{STATUS_LABELS[row.status]}</span>
          </dd>
          <dt>Submitted</dt>
          <dd>{new Date(row.submittedAt).toLocaleString()}</dd>
          <dt>Due</dt>
          <dd>
            {new Date(row.dueAt).toLocaleString()}{' '}
            {row.extendedAt && (
              <span style={{ color: 'var(--text-muted)' }}>
                (extended on {new Date(row.extendedAt).toLocaleDateString()} — {row.extensionReason})
              </span>
            )}
          </dd>
          {row.completedAt && (
            <>
              <dt>Completed</dt>
              <dd>{new Date(row.completedAt).toLocaleString()}</dd>
            </>
          )}
          <dt>Source</dt>
          <dd>{row.source}</dd>
          {row.assignedUserName && (
            <>
              <dt>Assigned to</dt>
              <dd>{row.assignedUserName}</dd>
            </>
          )}
        </dl>
        <h3>Requester's message</h3>
        <blockquote className="dsar-drawer__quote">{row.description}</blockquote>
      </section>

      {!canManage ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          You are signed in as a viewer / member; only owners and admins can update DSAR rows.
        </p>
      ) : TERMINAL.has(row.status) ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          This request is in a terminal state and cannot be modified. File a fresh request if a follow-up is needed.
        </p>
      ) : (
        <form className="dsar-form" onSubmit={submit} noValidate>
          <label className="dsar-form__field">
            <span className="dsar-form__label">Status</span>
            <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value as DsarStatus)}>
              {(Object.keys(STATUS_LABELS) as DsarStatus[]).map((k) => (
                <option key={k} value={k}>
                  {STATUS_LABELS[k]}
                </option>
              ))}
            </select>
          </label>

          <label className="dsar-form__field">
            <span className="dsar-form__label">Download URL (visible to requester)</span>
            <input
              type="url"
              value={downloadUrl}
              onChange={(e) => setDownloadUrl(e.target.value)}
              placeholder="https://…"
            />
            <span className="dsar-form__hint">
              Used for access / portability fulfilment — link a signed-URL export.
            </span>
          </label>

          <label className="dsar-form__field">
            <span className="dsar-form__label">Customer-facing notes</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          {nextStatus === 'rejected' && (
            <label className="dsar-form__field">
              <span className="dsar-form__label">Reason for rejection (required for "Rejected")</span>
              <textarea
                rows={3}
                required
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </label>
          )}

          <label className="dsar-form__field">
            <span className="dsar-form__label">Internal notes (operator-only)</span>
            <textarea rows={3} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} />
          </label>

          <fieldset className="dsar-drawer__extend">
            <legend>
              <Calendar size={14} /> Extend the Art. 12(3) clock
            </legend>
            <label className="dsar-form__field">
              <span className="dsar-form__label">Extra days (1–60)</span>
              <input
                type="number"
                min={0}
                max={60}
                value={extendDays}
                onChange={(e) => setExtendDays(Math.max(0, Math.min(60, Number(e.target.value) || 0)))}
              />
            </label>
            <label className="dsar-form__field">
              <span className="dsar-form__label">Reason (required when extending)</span>
              <textarea rows={2} value={extendReason} onChange={(e) => setExtendReason(e.target.value)} />
            </label>
          </fieldset>

          {err && (
            <div className="dsar-form__error" role="alert">
              <AlertTriangle size={14} /> {err}
            </div>
          )}

          <button type="submit" className="dsar-form__submit" disabled={busy}>
            {busy ? (
              <>
                <Loader2 size={14} className="dsar-form__spinner" /> Saving…
              </>
            ) : (
              <>
                <ShieldCheck size={14} /> Save changes
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
